import { useCallback, useEffect, useMemo } from 'react';
import { FetchError, SDKResponse } from '@commercetools/frontend-sdk';
import { Account, Address } from 'shared/types/account';
import type { SavedPaymentMethod, SetDefaultPaymentMethodResult, SetupPaymentResponse } from 'shared/types/payment';
import useSWR, { mutate } from 'swr';
import { sdk } from 'sdk';
import { revalidateOptions } from 'frontastic';
import { clearCustomizationData, getCustomizationData, setCustomizationData } from 'helpers/utils/customizationStorage';
import {
  GetAccountResult,
  RegisterAccount,
  RemoveAddressResult,
  UpdateAccount,
  UpdateUserPreferredLocaleResult,
} from './types';

const useAccount = (enabled = true) => {
  const extensions = sdk.composableCommerce;

  const result = useSWR(enabled ? '/action/account/getAccount' : null, () => extensions.account.getAccount(), {
    ...revalidateOptions,
    revalidateOnMount: enabled,
  });

  const data = useMemo(() => {
    const accountLoaded = !result.isLoading;

    if (result.data?.isError)
      return {
        loggedIn: false,
        accountLoading: false,
        accountLoaded,
        error: result.data.isError || result.error,
      };

    const account = (result.data?.data as GetAccountResult)?.account as Account;

    if (account?.accountId) return { account, loggedIn: true, accountLoading: false, accountLoaded };

    return {
      loggedIn: false,
      account: undefined,
      accountLoading: true,
      accountLoaded,
      error: result.data?.isError || result.error,
    };
  }, [result]);

  const accountId = data.account?.accountId;

  useEffect(() => {
    if (!accountId) return;

    const customizationData = getCustomizationData();
    const storedAccountId = typeof customizationData.accountId === 'string' ? customizationData.accountId : undefined;

    // If customization data belongs to a different authenticated user, reset it before continuing.
    if (storedAccountId && storedAccountId !== accountId) {
      clearCustomizationData();
      window.sessionStorage.removeItem('recentSearches');
      setCustomizationData({ accountId });
      return;
    }

    if (storedAccountId !== accountId) {
      setCustomizationData({
        ...customizationData,
        accountId,
      });
    }
  }, [accountId]);

  const shippingAddresses = useMemo(() => {
    if (!data.account) return [];

    return (data.account.addresses ?? []).filter((address) => address.isShippingAddress);
  }, [data.account]);

  const billingAddresses = useMemo(() => {
    if (!data.account) return [];

    return (data.account.addresses ?? []).filter((address) => address.isBillingAddress);
  }, [data.account]);

  const defaultShippingAddress = useMemo(() => {
    return shippingAddresses?.find((address) => address.isDefaultShippingAddress);
  }, [data.account]);

  const defaultBillingAddress = useMemo(() => {
    return billingAddresses?.find((address) => address.isDefaultBillingAddress);
  }, [data.account]);

  const allAddresses = useMemo(() => {
    if (!data.account) return [];

    return data.account.addresses ?? [];
  }, [data.account]);

  const login = async (email: string, password: string, remember?: boolean): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const payload = {
      email,
      password,
      remember,
    };

    const res = await extensions.account.login(payload);

    await mutate('/action/account/getAccount');
    await mutate('/action/cart/getCart');
    await mutate('/action/wishlist/getWishlist');

    return res.isError ? ({} as Account) : res.data;
  };

  const logout = useCallback(async () => {
    const extensions = sdk.composableCommerce;

    await extensions.account.logout();

    mutate('/action/account/getAccount');
    mutate('/action/cart/getCart');
    mutate('/action/wishlist/getWishlist');
  }, []);

  const register = useCallback(
    async (
      account: RegisterAccount,
    ): Promise<{ success: false; error: FetchError } | { success: true; account: Account }> => {
      const extensions = sdk.composableCommerce;

      const res = await extensions.account.register(account);

      return res.isError ? { success: false, error: res.error } : { success: true, account: res.data };
    },
    [],
  );

  const confirm = useCallback(async (token: string): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.confirm({ token });

    mutate('/action/account/getAccount');

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const requestConfirmationEmail = useCallback(async (email: string, password: string) => {
    const extensions = sdk.composableCommerce;

    const payload = {
      email,
      password,
    };

    const response = await extensions.account.requestConfirmationEmail(payload);

    return response.isError ? { error: true, message: response.error.message } : { error: false };
  }, []);

  const changePassword = useCallback(async (oldPassword: string, newPassword: string): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.changePassword({ oldPassword, newPassword });

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const requestPasswordReset = useCallback(async (email: string) => {
    const extensions = sdk.composableCommerce;

    const payload = {
      email,
    };

    const response = await extensions.account.requestResetPassword(payload);

    return response.isError ? { error: true, message: response.error.message } : { error: false };
  }, []);

  const resetPassword = useCallback(async (token: string, newPassword: string) => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.resetPassword({ token, newPassword });

    mutate('/action/account/getAccount');

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const update = useCallback(async (account: UpdateAccount): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.updateAccount(account);

    mutate('/action/account/getAccount');

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const addAddress = useCallback(async (address: Omit<Address, 'addressId'>): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.addAddress({ address });

    mutate('/action/account/getAccount');

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const addShippingAddress = useCallback(async (address: Omit<Address, 'addressId'>): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const response = await extensions.account.getAccount();

    if (response.isError || !response.data.loggedIn) return {} as Account;

    const res = await sdk.callAction<Account>({
      actionName: 'account/addShippingAddress',
      payload: { account: response.data.account, address },
    });

    mutate('/action/account/getAccount');

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const addBillingAddress = useCallback(async (address: Omit<Address, 'addressId'>): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const response = await extensions.account.getAccount();

    if (response.isError || !response.data.loggedIn) return {} as Account;

    const res = await sdk.callAction<Account>({
      actionName: 'account/addBillingAddress',
      payload: { account: response.data.account, address },
    });

    mutate('/action/account/getAccount');

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const updateAddress = useCallback(async (address: Address): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.updateAddress({ address });

    mutate('/action/account/getAccount');

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const removeAddress = useCallback(async (addressId: string): Promise<RemoveAddressResult> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.removeAddress({ addressId });

    mutate('/action/account/getAccount');

    return res.isError ? res : { isError: false, data: res.data };
  }, []);

  const setDefaultBillingAddress = useCallback(async (addressId: string): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.setDefaultBillingAddress({ addressId });

    mutate('/action/account/getAccount');

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const setDefaultShippingAddress = useCallback(async (addressId: string): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.setDefaultShippingAddress({ addressId });

    mutate('/action/account/getAccount');

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const deleteAccount = useCallback(async (password: string) => {
    const res = await sdk.composableCommerce.account.deleteAccount({ password });

    mutate('/action/account/getAccount');

    return { success: !res.isError };
  }, []);

  const gmsblogin = async (otp: string): Promise<SDKResponse<Account>> => {
    const extensions = sdk.composableCommerce;

    const payload = {
      otp,
    };

    const res = await extensions.account.gmsbLogin(payload);

    if (!res.isError) {
      await mutate('/action/account/getAccount', {
        ...res,
        data: {
          account: res.data,
          loggedIn: true,
        },
      });
    }

    return res;
  };

  const updateMarketingPreferences = useCallback(async (paylod: any): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const response = await extensions.account.getAccount();

    if (response.isError || !response.data.loggedIn) return {} as Account;

    const res = await sdk.callAction<Account>({
      actionName: 'account/updateMarketingPreferences',
      payload: paylod,
    });

    mutate('/action/account/getAccount');

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const updateUserPreferredLocale = useCallback(async (locale: string): Promise<UpdateUserPreferredLocaleResult> => {
    const extensions = sdk.composableCommerce;

    const response = await extensions.account.getAccount();

    if (response.isError || !response.data.loggedIn) return { success: false };

    const res = await sdk.callAction<UpdateUserPreferredLocaleResult>({
      actionName: 'account/updateUserPreferredLocale',
      payload: { locale },
    });

    return res.isError ? { success: false } : res.data;
  }, []);

  const createSetupIntent = useCallback(async (): Promise<SetupPaymentResponse> => {
    try {
      const response = await sdk.callAction<SetupPaymentResponse>({
        actionName: 'account/createSetupIntent',
      });

      if (response.isError) throw response.error;
      return response.data;
    } catch (e) {
      console.error('SetupPayment Error:', e);
      throw e;
    }
  }, []);

  const getSavedPaymentMethods = useCallback(
    async (): Promise<SavedPaymentMethod[]> => {
      try {
        const response = await sdk.callAction<SavedPaymentMethod[]>({
          actionName: 'account/getSavedPaymentMethods',
        });

        if (response.isError) throw response.error;
        return response.data;
      } catch (e) {
        console.error('Fetch Cards Error:', e);
        throw e;
      }
    },
    [sdk],
  );

  const setDefaultCard = useCallback(
    async (paymentMethodId: string): Promise<SetDefaultPaymentMethodResult> => {
      try {
        const response = await sdk.callAction<SetDefaultPaymentMethodResult>({
          actionName: 'account/setDefaultPaymentMethod',
          payload: { paymentMethodId },
        });
        if (response.isError) throw response.error;
        return response.data;
      } catch (e) {
        console.error('Delete Error:', e);
        throw e;
      }
    },
    [sdk],
  );

  const deletePaymentMethod = async (id: string) => {
    try {
      const response = await sdk.callAction({
        actionName: 'account/removePaymentMethod',
        payload: { paymentMethodId: id },
      });
      if (response.isError) throw response.error;
      return response.data;
    } catch (e) {
      console.error('Delete Error:', e);
      throw e;
    }
  };

  interface StripeVerifyResponse {
    message: string;
    paymentMethodId: string;
    customerId: string;
    isDuplicate: boolean;
  }

  const verifyPaymentMethod = async (setupIntentId: string) => {
    try {
      const response = await sdk.callAction<StripeVerifyResponse>({
        actionName: 'account/verifyPaymentMethod',
        payload: {
          setupIntentId,
        },
      });

      // Frontastic SDK returns an error object if the call fails
      if (response.isError) {
        // FIX: Access the message property or stringify the error
        const errorMessage = response.error?.message || 'Failed to verify payment method';
        throw new Error(errorMessage);
      }

      return response.data;
    } catch (error: any) {
      // Log the full error for debugging
      console.error('Error in verifyPaymentMethod hook:', error);

      // Re-throw as a standard string-based Error
      throw new Error(error.message || 'An unexpected error occurred');
    }
  };

  return {
    ...data,
    createSetupIntent,
    getSavedPaymentMethods,
    deletePaymentMethod,
    setDefaultCard,
    verifyPaymentMethod,
    allAddresses,
    shippingAddresses,
    billingAddresses,
    defaultShippingAddress,
    defaultBillingAddress,
    login,
    logout,
    register,
    confirm,
    requestConfirmationEmail,
    changePassword,
    deleteAccount,
    requestPasswordReset,
    resetPassword,
    update,
    addAddress,
    addBillingAddress,
    addShippingAddress,
    updateAddress,
    removeAddress,
    setDefaultBillingAddress,
    setDefaultShippingAddress,
    gmsblogin,
    updateMarketingPreferences,
    updateUserPreferredLocale,
  };
};

export default useAccount;
